import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import TransformIcon from '@mui/icons-material/Transform';
import StorageIcon from '@mui/icons-material/Storage';
import CalculateIcon from '@mui/icons-material/Calculate';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import SmartToyIcon from '@mui/icons-material/SmartToy';

const Landing: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const { clientX, clientY } = e;
        const { innerWidth, innerHeight } = window;
        const x = (clientX / innerWidth - 0.5) * 20;
        const y = (clientY / innerHeight - 0.5) * 20;
        heroRef.current.style.setProperty('--mouse-x', `${x}px`);
        heroRef.current.style.setProperty('--mouse-y', `${y}px`);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const services = [
    {
      icon: <TransformIcon sx={{ fontSize: 40 }} />,
      title: 'Digital Transformation',
      description: 'Retire legacy systems and outdated processes. We modernize your entire technology stack to drive efficiency and growth.',
    },
    {
      icon: <StorageIcon sx={{ fontSize: 40 }} />,
      title: 'Database & Systems Migration',
      description: 'Seamlessly migrate from outdated databases and manual processes to modern, scalable solutions without disrupting your business.',
    },
    {
      icon: <CalculateIcon sx={{ fontSize: 40 }} />,
      title: 'Complex Engineering Solutions',
      description: 'Advanced algorithms, mathematical modeling, and computational solutions for your most challenging technical problems.',
    },
    {
      icon: <IntegrationInstructionsIcon sx={{ fontSize: 40 }} />,
      title: 'Custom Software Development',
      description: 'Enterprise-grade applications built to handle complex business logic, integrations, and high-performance requirements.',
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: 40 }} />,
      title: 'AI Integration',
      description: 'Integrate AI capabilities into your applications using Model Context Protocol (MCP) and other frameworks for intelligent automation and decision support.',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: `
            radial-gradient(ellipse at 20% 20%, rgba(31, 111, 178, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(47, 62, 78, 0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, rgba(31, 111, 178, 0.05) 0%, transparent 70%),
            linear-gradient(180deg, #0a0f1a 0%, #111827 100%)
          `,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at calc(50% + var(--mouse-x, 0px)) calc(50% + var(--mouse-y, 0px)), 
                rgba(31, 111, 178, 0.12) 0%, 
                transparent 40%
              )
            `,
            pointerEvents: 'none',
            transition: 'background 0.1s ease-out',
          },
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            right: '10%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(31, 111, 178, 0.1) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(47, 62, 78, 0.15) 0%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'float 6s ease-in-out infinite reverse',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  animation: 'fadeInUp 0.8s ease-out',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: '#1f6fb2',
                    fontWeight: 600,
                    letterSpacing: 3,
                    mb: 2,
                    display: 'block',
                  }}
                >
                  DIGITAL TRANSFORMATION EXPERTS
                </Typography>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 3,
                    background: 'linear-gradient(135deg, #f8fafc 0%, #94a3b8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Retire Legacy.
                  <br />
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #1f6fb2 0%, #4a8fc7 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Embrace Innovation.
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#94a3b8',
                    fontWeight: 400,
                    mb: 4,
                    maxWidth: 540,
                    lineHeight: 1.7,
                  }}
                >
                  We don't just build websites—we transform businesses. From retiring outdated 
                  databases and manual processes to implementing complex engineering solutions, 
                  we deliver the technical expertise your business needs to thrive.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    component={Link}
                    to="/work"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                    }}
                  >
                    View Our Work
                  </Button>
                  <Button
                    component={Link}
                    to="/contact"
                    variant="outlined"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      color: '#f8fafc',
                      borderColor: 'rgba(248, 250, 252, 0.3)',
                      '&:hover': {
                        borderColor: '#f8fafc',
                        backgroundColor: 'rgba(248, 250, 252, 0.05)',
                      },
                    }}
                  >
                    Get in Touch
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  animation: 'fadeInUp 0.8s ease-out 0.2s both',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: -20,
                      background: 'radial-gradient(circle, rgba(31, 111, 178, 0.2) 0%, transparent 70%)',
                      borderRadius: '50%',
                      filter: 'blur(30px)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/AIM_logo_transparent.png"
                    alt="AIM - Always Inventing More"
                    sx={{
                      width: { xs: 250, md: 350 },
                      height: 'auto',
                      position: 'relative',
                      zIndex: 1,
                      filter: 'drop-shadow(0 20px 40px rgba(31, 111, 178, 0.3))',
                      animation: 'float 6s ease-in-out infinite',
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* AI Integration Section */}
      <Box
        sx={{
          py: { xs: 12, md: 16 },
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse at 30% 50%, rgba(168, 85, 247, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
            linear-gradient(180deg, #0a0f1a 0%, #0f0a1a 50%, #0a0f1a 100%)
          `,
        }}
      >
        {/* Animated particles/grid effect */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(168, 85, 247, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(168, 85, 247, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
          }}
        />
        
        {/* Floating orbs */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'float 7s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 250,
            height: 250,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
            filter: 'blur(50px)',
            animation: 'float 9s ease-in-out infinite reverse',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    background: 'linear-gradient(90deg, #a855f7 0%, #3b82f6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontWeight: 700,
                    letterSpacing: 3,
                    mb: 2,
                    display: 'block',
                  }}
                >
                  AI-POWERED SOLUTIONS
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 3,
                    color: '#f8fafc',
                  }}
                >
                  Intelligent Applications,{' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(90deg, #a855f7 0%, #3b82f6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Real Results
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#94a3b8',
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  We integrate AI directly into your business applications—not as a gimmick, 
                  but as a powerful tool that automates complex tasks, surfaces insights, 
                  and augments your team's capabilities.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#94a3b8',
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    mb: 4,
                  }}
                >
                  Using the <strong style={{ color: '#a855f7' }}>Model Context Protocol (MCP)</strong> and 
                  modern AI frameworks, we build intelligent features that understand your domain, 
                  connect to your data, and deliver measurable value.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    background: 'linear-gradient(90deg, #a855f7 0%, #3b82f6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #9333ea 0%, #2563eb 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 30px rgba(168, 85, 247, 0.4)',
                    },
                  }}
                >
                  Explore AI Integration
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {[
                  { 
                    title: 'Intelligent Automation', 
                    desc: 'Automate documentation, data entry, and repetitive tasks with AI that understands context.',
                  },
                  { 
                    title: 'Smart Decision Support', 
                    desc: 'Surface insights and recommendations based on your business data and domain expertise.',
                  },
                  { 
                    title: 'Natural Language Interfaces', 
                    desc: 'Let users interact with your systems using plain language queries and commands.',
                  },
                  { 
                    title: 'Domain-Specific AI', 
                    desc: 'Custom AI solutions trained and tuned for your specific industry and use cases.',
                  },
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'rgba(168, 85, 247, 0.05)',
                        border: '1px solid rgba(168, 85, 247, 0.15)',
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(168, 85, 247, 0.1)',
                          borderColor: 'rgba(168, 85, 247, 0.3)',
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <SmartToyIcon sx={{ color: '#a855f7', mb: 1.5, fontSize: 28 }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 600,
                          fontSize: '1rem',
                          mb: 1,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#94a3b8',
                          lineHeight: 1.6,
                          fontSize: '0.875rem',
                        }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What We Solve Section */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: 'linear-gradient(180deg, #111827 0%, #0a0f1a 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#1f6fb2',
                    fontWeight: 600,
                    letterSpacing: 3,
                    mb: 2,
                    display: 'block',
                  }}
                >
                  THE PROBLEM
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 600,
                    color: '#f8fafc',
                    mb: 3,
                  }}
                >
                  Still Running on Spreadsheets & Legacy Systems?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#94a3b8',
                    lineHeight: 1.8,
                    mb: 2,
                  }}
                >
                  Many businesses are held back by outdated technology—manual data entry, 
                  disconnected spreadsheets, aging databases, and processes that haven't 
                  changed in decades.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#94a3b8',
                    lineHeight: 1.8,
                  }}
                >
                  These inefficiencies cost time, money, and competitive advantage. 
                  It's time for a change.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {[
                  { icon: <AutorenewIcon />, text: 'Manual processes eating up hours' },
                  { icon: <StorageIcon />, text: 'Disconnected data silos' },
                  { icon: <ArchitectureIcon />, text: 'Outdated architecture limiting growth' },
                  { icon: <CalculateIcon />, text: 'Complex calculations done by hand' },
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2.5,
                        borderRadius: 2,
                        background: 'rgba(239, 68, 68, 0.05)',
                        border: '1px solid rgba(239, 68, 68, 0.15)',
                      }}
                    >
                      <Box sx={{ color: '#ef4444' }}>{item.icon}</Box>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        {item.text}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: `
            radial-gradient(ellipse at 50% 0%, rgba(31, 111, 178, 0.08) 0%, transparent 50%),
            linear-gradient(180deg, #0a0f1a 0%, #111827 100%)
          `,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#1f6fb2',
                fontWeight: 600,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
              }}
            >
              OUR EXPERTISE
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 600,
                color: '#f8fafc',
                mb: 2,
              }}
            >
              More Than Just Web Development
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#94a3b8',
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              We bring deep technical expertise to solve complex business challenges—from 
              advanced mathematical modeling to enterprise system migrations.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(31, 111, 178, 0.2)',
                      '& .service-icon': {
                        transform: 'scale(1.1)',
                        color: '#1f6fb2',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      <Box
                        className="service-icon"
                        sx={{
                          color: '#94a3b8',
                          transition: 'all 0.3s ease',
                          flexShrink: 0,
                        }}
                      >
                        {service.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 600,
                            mb: 1.5,
                          }}
                        >
                          {service.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#94a3b8',
                            lineHeight: 1.7,
                          }}
                        >
                          {service.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 12 },
          background: `
            radial-gradient(ellipse at 50% 50%, rgba(31, 111, 178, 0.1) 0%, transparent 60%),
            linear-gradient(180deg, #111827 0%, #0a0f1a 100%)
          `,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 600,
              color: '#f8fafc',
              mb: 3,
            }}
          >
            Ready to modernize your business?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#94a3b8',
              mb: 4,
              maxWidth: 550,
              mx: 'auto',
            }}
          >
            Let's discuss how we can retire your legacy systems and build 
            efficient, scalable solutions that drive real business results.
          </Typography>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              px: 5,
              py: 1.5,
              fontSize: '1.1rem',
            }}
          >
            Start Your Transformation
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
